import { useTranslations } from "next-intl";
import { MealTagList } from "@/components/meal-tag-list";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { DietMenuModalAllergensAlert } from "@/features/diets/components/diet-menu-modal/diet-menu-modal-allergens-alert";
import type { DietCatalogDetail } from "@/features/diets/types";
import type { DietMenuPreviewMeals } from "@/features/diets/types/diet-menu-preview.type";
import type { Dish } from "@/features/panel/types";
import { cn } from "@/lib/utils";

type DietMenuItemProps = {
  meal: DietMenuPreviewMeals;
  setActiveMealIngredients: (mealId: string, dishId: string) => void;
  activeAllergensAlert?: boolean;
  dish: Dish | undefined;
  menuSelection?: boolean;
  data: DietCatalogDetail;
};

export const DietMenuItem = ({
  meal,
  setActiveMealIngredients,
  activeAllergensAlert,
  dish,
  menuSelection = false,
  data,
}: DietMenuItemProps) => {
  const t = useTranslations();

  const nutritions = [
    {
      label: menuSelection
        ? t("nutrition.proteinShort")
        : t("nutrition.protein"),
      shortLabel: t("nutrition.proteinShort"),
      value: dish?.nutrition.protein,
      unit: "g",
    },
    {
      label: menuSelection ? t("nutrition.carbsShort") : t("nutrition.carbs"),
      shortLabel: t("nutrition.carbsShort"),
      value: dish?.nutrition.carbs,
      unit: "g",
    },
    {
      label: menuSelection ? t("nutrition.fatShort") : t("nutrition.fat"),
      shortLabel: t("nutrition.fatShort"),
      value: dish?.nutrition.fat,
      unit: "g",
    },
  ];

  return (
    <div
      className={cn(
        "flex h-full flex-col",
        activeAllergensAlert ? "-space-y-5" : "space-y-0",
      )}
    >
      <Card
        className={cn(
          "relative z-10 flex flex-1 flex-col border bg-background px-5 py-5 md:px-6",
          menuSelection ? "min-h-[230px]" : "min-h-[150px]",
        )}
      >
        <CardHeader
          className={cn(
            "w-full justify-between p-0",
            menuSelection ? "flex-1 flex-col" : "flex-row",
          )}
        >
          <div className="min-w-0 flex-1 space-y-2">
            {!menuSelection && (
              <MealTagList
                expandLabel={t("common.showMoreTags")}
                tags={data.tags.map((tag) => t(`dietTags.${tag.slug}`))}
              />
            )}
            {dish && (
              <p
                className={cn(
                  "pt-1 text-brand-green1",
                  menuSelection && "line-clamp-3 w-[90%]",
                )}
              >
                {dish.name}
              </p>
            )}
          </div>
          <div>
            <Button
              className="m-0 min-h-0 p-0"
              onClick={() =>
                dish && setActiveMealIngredients(meal.mealId, dish.id)
              }
              variant="link"
            >
              {t("diets.ingredients.details")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="m-0 mt-5 border-t p-0 pt-4 font-light">
          <div className="flex flex-wrap items-center gap-2 text-brand-green1 text-sm sm:gap-2.5 lg:gap-3">
            <div className="rounded-sm bg-brand-green-light2 px-3 py-1">
              <p className="text-sm">
                <span className="font-bold">{dish?.kcal}</span> kcal
              </p>
            </div>
            {nutritions.map((nutrition) => (
              <span className="flex gap-1" key={nutrition.label}>
                <span className="font-normal md:hidden">
                  {nutrition.shortLabel}
                </span>
                <span className="hidden font-normal md:inline">
                  {nutrition.label}
                </span>
                <span className="font-bold">
                  {nutrition.value} {nutrition.unit}
                </span>
              </span>
            ))}
          </div>
        </CardContent>
        <CardFooter className="p-0">
          <MealTagList
            className="w-full"
            expandLabel={t("common.showMoreTags")}
            tags={(dish?.tags ?? []).map((tag) => tag.label)}
          />
        </CardFooter>
      </Card>
      {activeAllergensAlert && (
        <DietMenuModalAllergensAlert
          allergens={dish?.allergens.allergens}
          className={cn(
            "px-4 pt-8 pb-4 md:px-5 md:pb-3",
            activeAllergensAlert ? "flex min-h-17" : "hidden h-0",
          )}
        />
      )}
    </div>
  );
};
